import React, { FC, useEffect, useState } from "react";
import { Button, Form, Input, FormInstance, Spin, Select, Col, Row, Tooltip, notification } from "antd";
import styled from "@emotion/styled";
import { CustomModal } from "../../utils/StyledComponents";
import { LanguageData } from "../../videos/modals/AddVideosMetaDataForm";
import { copyString, getLanguageList, getVideoDataByGracenoteId } from "../../utils";
import { CopyOutlined } from "@ant-design/icons";
import { CustomBtn } from "../../series/StyledComponent";

export const SubmitButton = styled(Button)`
	color: #000000 !important;
	font-weight: 600;
	background: #feea3c !important;
	border: 5px solid #feea3c !important;
	display: flex;
	justify-content: center;
	align-items: center;
`;

type Props = {
	isMetaFormOpen: boolean;
	saveMetaData(): void;
	form: FormInstance;
	isLoading: boolean;
	setIsLoading(value: boolean): void;
	handleCancel: () => void;
	editId: string | null;
};

const AddSeriesForm: FC<Props> = ({ editId, isMetaFormOpen, saveMetaData, form, isLoading, handleCancel }) => {
	const [languagesList, setLanguageList] = useState<LanguageData[]>([]);
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);

	useEffect(() => {
		getLanguageList(setLanguageList);
	}, []);

	const updateWithGracenoteId = async () => {
		try {
			const gracenoteId = form.getFieldValue(`gracenote_episodic_tms_id`);
			if (!gracenoteId) {
				return notification.error({
					message: "Gracenote Episode TMS Id not found",
					description: "Please fill in the Gracenote Episode TMS Id",
				});
			}
			setIsFormLoading(true);
			const { data } = await getVideoDataByGracenoteId(gracenoteId);
			const { description, title } = data;
			const gracenoteData: any = {
				title,
				description_full: description,
			}
			const metadataFields = ["title", "description_full"]
			const currentData = form.getFieldsValue(metadataFields);
			const updatedFormData: any = {};

			metadataFields.forEach((field: string) => {
				updatedFormData[field] =
					currentData[field] && String(currentData[field]).length
						? currentData[field]
						: gracenoteData[field];
			});
			form.setFieldsValue(updatedFormData);
			notification.success({
				message: null,
				description: "Data filled successfully in respective fields.",
			});
		} catch (error: any) {
			// setIsFormLoading(false);
			if (error?.response?.status === 404)
				return notification.error({
					message: null,
					description: "Provided Gracenote Episode TMS Id does not exist",
				});
			console.error("error in updateWithGracenoteId", error);
		} finally {
			setIsFormLoading(false);
		}
	};

	return (
		<CustomModal
			ref_id="dummy_ref_id"
			title={editId ? "Update Meta Info..." : "Add Meta Info..."}
			open={isMetaFormOpen}
			onOk={() => saveMetaData()}
			onCancel={() => handleCancel()}
			okText={editId ? "Update Metadata" : "Save Metadata"}
			maskClosable={false}
			okButtonProps={{ disabled: isLoading }}
			cancelButtonProps={{ disabled: isLoading }}
			centered
		>
			<Spin spinning={isLoading}>
				<Form form={form} name="basic" autoComplete="off" layout="vertical">
					<Row>
						<Col span={11}>
							<Form.Item
								label="Language"
								key="language"
								name="language"
								rules={[{ required: true, message: "Required Field" }]}
							>
								<Select placeholder="Language">
									{languagesList?.map((language: LanguageData) => (
										<Select.Option value={language.NAME} key={language.ID}>
											{language.NAME}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col span={12} offset={1}>
							<Form.Item
								label="Ref ID"
								name="ref_id"
								rules={[{ required: true, message: "Required Field" }]}
							>
								<Input placeholder="Ref ID" />
							</Form.Item>
						</Col>
					</Row>
					<Form.Item
						label="Title"
						name="title"
						rules={[{ required: true, message: "Required Field" }]}
					>
						<Input placeholder="Title" showCount maxLength={150} />
					</Form.Item>

					<Form.Item label="Clean Title" name="clean_title">
						<Input />
					</Form.Item>
					<Form.Item
						label="Keywords"
						key="keywords"
						name="keywords"
						// rules={[{ required: true, message: "Required Field" }]}
					>
						<Input placeholder="Keywords" />
					</Form.Item>
					<Form.Item label="Description 100" name="description_100">
						<Input.TextArea showCount maxLength={100} />
					</Form.Item>
					<Form.Item
						label="Description 250"
						name="description_250"
						// rules={[{ required: true, message: "Required Field" }]}
					>
						<Input.TextArea showCount maxLength={250} />
					</Form.Item>
					<Form.Item
						label="Full Description"
						name="description_full"
						// rules={[{ required: true, message: "Required Field" }]}
					>
						<Input.TextArea showCount />
					</Form.Item>
					<Row align="middle">
						<Col span={14}>
							<Form.Item
								label="Gracenote Episodic TMS Id"
								name={"gracenote_episodic_tms_id"}
							>
								<Input 
									placeholder="Enter Gracenote Episodic TMS ID" 
									suffix={
										<CustomBtn
											style={{ cursor: "copy" }}
											title="Copy Gracenote Episodic TMS Id"
											icon={<CopyOutlined />}
											type={"link"}
											onClick={e => {
												e.stopPropagation();
												copyString(form.getFieldValue("gracenote_episodic_tms_id"));
											}}
										/>
									}
								/>
							</Form.Item>
						</Col>
						<Col span={10}>
							<Tooltip title="Fills in empty data in both this video, and the parent episode, from Gracenote using the Gracenote Episode TMS Id to find the episode.">
								<Button style={{marginLeft: "8px"}} loading={isFormLoading} onClick={updateWithGracenoteId} type="primary">
									Gracenote Update
								</Button>
							</Tooltip>
						</Col>
					</Row>
				</Form>
			</Spin>
		</CustomModal>
	);
};

export default AddSeriesForm;
